import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'updated'>('updated');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredComics = useMemo(() => {
    let result = slug ? getComicsByCategory(slug) : comics;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = comics.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      case 'views':
        return [...result].sort((a, b) => b.views - a.views);
      case 'updated':
        return [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      default:
        return result;
    }
  }, [slug, sortBy, searchQuery]);

  const pageTitle = searchQuery
    ? `搜索"${searchQuery}"的结果`
    : currentCategory
      ? `${currentCategory.name}漫画`
      : '全部分类';

  const pageDescription = currentCategory
    ? `浏览韩漫网站${currentCategory.name}分类下的韩国漫画免费资源，韩漫大全中的${currentCategory.name}漫画一网打尽，每日更新，免费阅读。`
    : '';

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://hanman.example.com/category/${slug || ''}`,
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={`韩漫网站,韩国漫画免费,韩漫网址,韩漫大全${currentCategory ? `,${currentCategory.name}漫画` : ''}`}
        canonicalUrl={`https://hanman.example.com/category/${slug || ''}`}
      />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            ...(searchQuery
              ? [{ label: '分类', href: '/category/romance' }, { label: `搜索: ${searchQuery}` }]
              : currentCategory
                ? [{ label: '分类', href: '/category/romance' }, { label: currentCategory.name }]
                : [{ label: '全部分类' }]
            ),
          ]}
        />

        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {pageTitle}
          </h1>
          {currentCategory && (
            <p className="text-sm text-foreground-500 mt-2">
              韩漫网站{currentCategory.name}分类，共{filteredComics.length}部韩国漫画免费阅读
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                slug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} mr-1.5 text-xs`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500">排序:</span>
            {[
              { key: 'updated' as const, label: '最新' },
              { key: 'rating' as const, label: '评分' },
              { key: 'views' as const, label: '人气' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === s.key
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {filteredComics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {filteredComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-search-line text-3xl text-foreground-400"></i>
            </div>
            <h3 className="text-lg font-medium text-foreground-700 mb-2">没有找到相关漫画</h3>
            <p className="text-sm text-foreground-500 mb-6">试试其他关键词或浏览分类</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        )}
      </div>
    </>
  );
}